import { reasonCopy } from './outcome-copy';
import { clientSheets } from './report-sections';
import { plural } from './report-values';

import type { FinalReport, FinalReportRow } from './final-report';
import type { ModuleReportView, Observation } from './module-report-types';

/**
 * The report's "Observations": what is worth a sentence across all clients.
 * The module's own come first (it knows what its warnings mean); then the
 * generic ones — warnings and failures by code, why clients were not
 * migrated, capped record lists, runs nobody has read yet.
 */

interface Tally {
    readonly code: string;
    readonly count: number;
    readonly message: string;
}

/** Grouped by code, in first-seen order, each with the first message seen. */
function byCode(items: readonly { code: string; message: string }[]): Tally[] {
    const tallies = new Map<string, Tally>();
    for (const item of items) {
        const known = tallies.get(item.code);
        tallies.set(item.code, {
            code: item.code,
            count: (known?.count ?? 0) + 1,
            message: known?.message ?? item.message,
        });
    }
    return [...tallies.values()];
}

function rowIssue(rows: readonly FinalReportRow[], category: FinalReportRow['category']) {
    return rows
        .filter((row) => row.category === category)
        .map((row) => ({
            code: row.reason ?? 'UNKNOWN',
            message:
                (category === 'skipped' ? reasonCopy(row.reason) : null) ??
                row.message ??
                'No message.',
        }));
}

function tallied(label: string, unit: string, tallies: readonly Tally[]): Observation[] {
    return tallies.map((tally) => ({
        label,
        code: tally.code,
        text: `${plural(tally.count, unit)}: ${tally.message}`,
    }));
}

export function genericObservations(
    report: Pick<FinalReport, 'rows' | 'header'>,
    explained: ReadonlySet<string> = new Set(),
): Observation[] {
    const { rows } = report;
    const tracking = rows.flatMap((row) => row.records.map((record) => record.tracking));
    const warnings = tracking
        .flatMap((track) => track?.warnings ?? [])
        .filter((warning) => !explained.has(warning.code));
    const failures = tracking.flatMap((track) => (track?.failure ? [track.failure] : []));

    const capped = rows.filter((row) => row.recordsTotal > row.records.length);
    const unread = rows.filter((row) => row.category === 'pending_refresh');

    return [
        ...tallied('Failures', 'record', byCode(failures)),
        ...tallied('Warnings', 'warning', byCode(warnings)),
        ...tallied('Not migratable', 'client', byCode(rowIssue(rows, 'not_migratable'))),
        ...tallied('Skipped', 'client', byCode(rowIssue(rows, 'skipped'))),
        ...tallied('Errors', 'client', byCode(rowIssue(rows, 'error'))),
        ...tallied('Status unreadable', 'client', byCode(rowIssue(rows, 'refresh_failed'))),
        ...(capped.length === 0
            ? []
            : [
                  {
                      label: 'Records capped',
                      code: null,
                      text:
                          `${plural(capped.length, 'client has', 'clients have')} more records ` +
                          'than the report holds; it shows the first ones the runner kept, and ' +
                          'so do the CSV and JSON downloads.',
                  },
              ]),
        ...(unread.length === 0
            ? []
            : [
                  {
                      label: 'Not refreshed',
                      code: null,
                      text:
                          `${plural(unread.length, 'launched client has', 'launched clients have')} ` +
                          'no finished status refresh. Refresh the migration to read their runs.',
                  },
              ]),
        ...(report.header.refreshLookupLimited
            ? [
                  {
                      label: 'Refresh lookup',
                      code: null,
                      text:
                          'No finished status refresh of this launch is among the module’s ' +
                          'newest jobs, the most the runner lists. If one ran earlier, it is ' +
                          'not read here: refresh the migration to read every run again.',
                  },
              ]
            : []),
    ];
}

export function reportObservations(report: FinalReport, view: ModuleReportView): Observation[] {
    return [
        ...view.observations(clientSheets(report)),
        ...genericObservations(report, view.explainedWarningCodes),
    ];
}
